/*
 * @flow strict-local
 * Copyright (C) 2020 MetaBrainz Foundation
 *
 * This file is part of MusicBrainz, the open internet music database,
 * and is licensed under the GPL version 2, or (at your option) any
 * later version: http://www.gnu.org/licenses/gpl-2.0.txt
 */

import * as React from 'react';

import {expect} from '../../../../utility/invariant.js';
import ButtonPopover from '../../common/components/ButtonPopover.js';
import {ERROR_TARGETS} from '../../edit/URLCleanup.js';
import type {LinksEditorActionT, LinkStateT} from '../types.js';

component URLInputPopoverContent(
  dispatch: (LinksEditorActionT) => void,
  link: LinkStateT,
) {
  const pendingLink = expect(link.urlPopoverLinkState);
  const url = pendingLink.url;
  const rawUrl = pendingLink.rawUrl;
  const error = pendingLink.error;

  const acceptDialog = React.useCallback(() => {
    dispatch({link, type: 'accept-url-input-popover'});
  }, [dispatch, link]);

  const cancelDialog = React.useCallback(() => {
    dispatch({link, type: 'cancel-url-input-popover'});
  }, [dispatch, link]);

  const handleUrlChange = React.useCallback((
    event: SyntheticEvent<HTMLInputElement>,
  ) => {
    const rawUrl = event.currentTarget.value;
    dispatch({
      link,
      rawUrl,
      type: 'update-url-input-popover-url',
    });
  }, [dispatch, link]);

  return (
    <form
      onSubmit={(event: SyntheticEvent<HTMLFormElement>) => {
        event.preventDefault();
        // Prevent the submit event from propagating to the parent form.
        event.stopPropagation();
        acceptDialog();
      }}
    >
      <table>
        <tbody>
          <tr>
            <td className="section">
              {addColonText(l('URL'))}
            </td>
            <td>
              <input
                className="value raw-url"
                onChange={handleUrlChange}
                style={{width: '336px'}}
                value={rawUrl}
              />
              {error && error.target === ERROR_TARGETS.URL ? (
                <div
                  className="error field-error target-url"
                  data-visible="1"
                >
                  {error.message}
                </div>
              ) : null}
            </td>
          </tr>
          {url ? (
            <tr>
              <td className="section" style={{whiteSpace: 'nowrap'}}>
                {addColonText(l('Cleaned up to'))}
              </td>
              <td>
                {error ? url : (
                  <a
                    className="clean-url"
                    href={url}
                    rel="noreferrer"
                    style={{overflowWrap: 'anywhere'}}
                    target="_blank"
                  >
                    {url}
                  </a>)}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
      <div className="buttons" style={{display: 'block', marginTop: '1em'}}>
        <button
          className="negative"
          onClick={cancelDialog}
          type="button"
        >
          {l('Cancel')}
        </button>
        <div className="buttons-right">
          <button
            className="positive"
            onClick={acceptDialog}
            type="button"
          >
            {l('Done')}
          </button>
        </div>
      </div>
    </form>
  );
}

component _URLInputPopover(
  dispatch: (LinksEditorActionT) => void,
  link: LinkStateT,
) {
  const pendingLink = link.urlPopoverLinkState;

  const toggle = React.useCallback((open: boolean) => {
    // Will be called by ButtonPopover when closed by losing focus.
    if (open) {
      dispatch({link, type: 'open-url-input-popover'});
    } else {
      dispatch({link, type: 'accept-url-input-popover'});
    }
  }, [dispatch, link]);

  const buildPopoverChildren = React.useCallback(() => (
    <URLInputPopoverContent
      dispatch={dispatch}
      link={link}
    />
  ), [dispatch, link]);

  return (
    <ButtonPopover
      buildChildren={buildPopoverChildren}
      buttonContent={null}
      buttonProps={{
        className: 'icon edit-item',
        title: lp('Edit URL', 'interactive'),
      }}
      id="url-input-popover"
      isDisabled={!link.isSubmitted}
      isOpen={pendingLink !== null}
      toggle={toggle}
    />
  );
}

const URLInputPopover:
  component(...React.PropsOf<_URLInputPopover>) =
    React.memo(_URLInputPopover);

export default URLInputPopover;
